<script setup lang="ts">
/**
 * The home screen's hints (MAR-933) — one quiet row of "Prueba: …" chips under the composer, each
 * linking to where a thing lives, plus an × that hides the row for good.
 *
 * It replaced the first-move cards (#81): their adaptive "import a document" / "create your first
 * case" hero is the job the onboarding flow and its reminders own now, and a card grid pushed the
 * recent activity under the fold. The row only POINTS; it never starts a flow.
 *
 * PLAN-AWARE: a chip exists only for a feature the org's plan includes, read off the same
 * `feature_flags` the rail gates its entries on — never a link to a page that would 403.
 */
import { X } from 'lucide-vue-next'

const emit = defineEmits<{ dismiss: [] }>()

const { t } = useI18n()
const { profile } = useUserProfile()
const { docsDrawerOpen, toggleDocsDrawer } = usePanelLayout()

interface Hint { key: string; label: string; to?: string; action?: () => void }

const hints = computed<Hint[]>(() => {
  const flags = profile.value?.feature_flags
  const list: Hint[] = [
    { key: 'laws', label: t('onboarding.hints.laws'), to: '/app/laws' },
    { key: 'documents', label: t('onboarding.hints.documents'), action: () => { if (!docsDrawerOpen.value) toggleDocsDrawer() } },
  ]
  if (flags?.case_management) list.push({ key: 'cases', label: t('onboarding.hints.cases'), to: '/app/clients' })
  // A review starts from its document (MAR-1097), so the hint opens the documents, like the one above.
  if (flags?.contract_review) list.push({ key: 'review', label: t('onboarding.hints.review'), action: () => { if (!docsDrawerOpen.value) toggleDocsDrawer() } })
  if (flags?.sheets) list.push({ key: 'sheets', label: t('onboarding.hints.sheets'), to: '/app/sheets' })
  if (flags?.workflows) list.push({ key: 'workflows', label: t('onboarding.hints.workflows'), to: '/app/workflows' })
  return list
})

/*
 * A 44×44 TARGET FOR EVERY POINTER, a 32px chip to the eye (MAR-933 §20; the mockup's quiet row).
 * The link/button itself is the hit area — 44px tall, transparent, `group` — and the visible pill is
 * the span inside it, so the look did not grow with the target. It used to be 44px only under
 * `pointer-coarse`, which left every mouse user a 32px target.
 *
 * ON A TOUCH SCREEN THE LOOK IS WHAT IT WAS (tester F1, decided by Alex 2026-09-26): under
 * `pointer-coarse` the pill and the ×'s square are themselves 44px, filling the target, and wrapped
 * rows keep their 8px gap — exactly develop's touch layout. Only a fine pointer gets the quiet 32px
 * pill inside a 44px box.
 */
const chipClass = 'group inline-flex min-h-11 min-w-11 items-center justify-center rounded-full focus-visible:outline-none'
const pillClass = 'inline-flex min-h-8 pointer-coarse:min-h-11 items-center rounded-full border px-3 py-1 text-xs text-muted-foreground transition-colors group-hover:border-accent-ink group-hover:text-foreground group-focus-visible:ring-2 group-focus-visible:ring-ring'
</script>

<template>
  <!-- `gap-x-2`, no row gap under a mouse: the 44px targets already space wrapped rows apart. On touch the pills fill their targets, so the 8px row gap comes back. -->
  <nav class="flex w-full flex-wrap items-center gap-x-2 pointer-coarse:gap-y-2" :aria-label="t('onboarding.hints.label')" data-testid="home-hints">
    <span class="text-xs text-muted-foreground">{{ t('onboarding.hints.try') }}</span>
    <template v-for="hint in hints" :key="hint.key">
      <NuxtLink v-if="hint.to" :to="hint.to" :class="chipClass" :data-hint="hint.key"><span :class="pillClass">{{ hint.label }}</span></NuxtLink>
      <button v-else type="button" :class="chipClass" :data-hint="hint.key" @click="hint.action?.()"><span :class="pillClass">{{ hint.label }}</span></button>
    </template>
    <!-- The same split: a 44×44 button, a 32px square that lights up on hover. -->
    <button
      type="button"
      class="group ml-auto inline-flex h-11 w-11 items-center justify-center rounded-md text-muted-foreground focus-visible:outline-none"
      :title="t('onboarding.hints.dismiss')"
      :aria-label="t('onboarding.hints.dismiss')"
      @click="emit('dismiss')"
    >
      <span class="inline-flex h-8 w-8 pointer-coarse:h-11 pointer-coarse:w-11 items-center justify-center rounded-md transition-colors group-hover:bg-accent-lighter group-hover:text-foreground group-focus-visible:ring-2 group-focus-visible:ring-ring">
        <X class="h-3.5 w-3.5" />
      </span>
    </button>
  </nav>
</template>
